import { ArrowLeft, Banknote, CreditCard, Loader2 } from 'lucide-react'
import { useState } from 'react'

import { Money } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PaymentType } from '@invo.space/shared'
import { cn } from '@/lib/utils'

/** Zaokrouhlené bankovky nad částkou, jako rychlé volby "Přijato". */
const quickAmounts = (total: number) => {
  const steps = [100, 200, 500, 1000, 2000, 5000]
  const out = new Set<number>()
  for (const step of steps) {
    const v = Math.ceil(total / step) * step
    if (v > total) out.add(v)
    if (out.size >= 3) break
  }
  return Array.from(out)
}

const parseAmount = (value: string) => {
  const n = Number(value.replace(/\s/g, '').replace(',', '.'))
  return Number.isFinite(n) ? n : NaN
}

/**
 * Potvrzení platby hotově nebo kartou. Účtenka se uloží až po "Dokončit platbu",
 * do té doby jde způsob platby změnit.
 */
export const PaymentConfirm = ({
  type,
  total,
  busy,
  onConfirm,
  onBack,
}: {
  type: PaymentType
  total: number
  busy: boolean
  onConfirm: () => void
  onBack: () => void
}) => {
  const [received, setReceived] = useState('')
  const isCash = type === PaymentType.Cash
  const receivedNum = parseAmount(received)
  const change = received !== '' && Number.isFinite(receivedNum) ? receivedNum - total : null

  return (
    <div className="grid gap-3" role="group" aria-label={isCash ? 'Platba hotově' : 'Platba kartou'}>
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-background text-muted-foreground">
          {isCash ? <Banknote className="size-4" aria-hidden /> : <CreditCard className="size-4" aria-hidden />}
        </span>
        <p className="min-w-0 flex-1 text-sm font-medium">{isCash ? 'Platba hotově' : 'Platba kartou'}</p>
        <Money amount={total} minor={false} />
      </div>

      {isCash ? (
        <div className="grid gap-2">
          <div className="grid grid-cols-[minmax(0,1fr)_repeat(3,auto)] items-center gap-2">
            <label htmlFor="cash-received" className="sr-only">
              Přijato od zákazníka
            </label>
            <Input
              id="cash-received"
              inputMode="decimal"
              placeholder="Přijato, Kč"
              value={received}
              onChange={(e) => setReceived(e.target.value.replace(/[^\d,.\s]/g, ''))}
              className="h-11 min-w-0 bg-background text-base tabular"
            />
            {quickAmounts(total).map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => setReceived(String(v))}
                className="h-11 rounded-full bg-background px-3 text-sm font-medium tabular hover:bg-background/70"
              >
                {v.toLocaleString('cs-CZ')}
              </button>
            ))}
          </div>
          <p className={cn('text-sm', change !== null && change < 0 ? 'text-danger' : 'text-muted-foreground')} aria-live="polite">
            {change === null ? (
              'Zadejte přijatou částku a uvidíte, kolik vrátit. Není to povinné.'
            ) : change < 0 ? (
              <>
                Chybí <Money amount={-change} minor={false} size="sm" className="text-danger" />
              </>
            ) : (
              <>
                Vrátit zákazníkovi <Money amount={change} minor={false} size="sm" className="text-foreground" />
              </>
            )}
          </p>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Nechte zákazníka zaplatit na terminálu, pak platbu dokončete.</p>
      )}

      <div className="grid grid-cols-[auto_minmax(0,1fr)] gap-2">
        <Button variant="outline" className="h-14 gap-1.5 bg-background px-4 text-base" onClick={onBack} disabled={busy}>
          <ArrowLeft />
          Změnit
        </Button>
        <Button className="h-14 text-base" onClick={onConfirm} disabled={busy || (change !== null && change < 0)}>
          {busy && <Loader2 className="size-5 animate-spin" aria-hidden />}
          Dokončit platbu
        </Button>
      </div>
    </div>
  )
}
